// Real-user Core Web Vitals -> POST /client-vitals.
//
// Written against PerformanceObserver directly rather than pulling in the
// web-vitals package: the four numbers we care about are ~60 lines of
// observers, and a telemetry beacon that ships its own dependency would be
// measuring a bundle it made bigger.
//
// The whole reason this exists is the `device` tag. Tesla center-screen
// performance has only ever been argued about; now it is a p75 next to
// desktop's p75 in Admin -> Performance.
//
// One beacon per page view, sent on the first hide (visibilitychange or
// pagehide) via sendBeacon so it survives the tab going away. No identity is
// attached, and dynamic path segments are collapsed so a route -- not a
// specific vehicle -- is what gets recorded.

import { isTeslaBrowser, isCoarsePointer } from "./device.js";

const ENDPOINT = "/client-vitals";

const THRESHOLDS = {
  LCP: [2500, 4000],
  INP: [200, 500],
  CLS: [0.1, 0.25],
  TTFB: [800, 1800],
  FCP: [1800, 3000],
};

function rate(metric, value) {
  const t = THRESHOLDS[metric];
  if (!t) return "";
  if (value <= t[0]) return "good";
  if (value <= t[1]) return "needs-improvement";
  return "poor";
}

// /vehicles/8f3c-... -> /vehicles/:id ; /link/<token> -> /link/:token
function routeOf(pathname) {
  return String(pathname || "/")
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg)) return ":id";
      if (/^\d+$/.test(seg)) return ":id";
      if (seg.length >= 20) return ":token";
      return seg;
    })
    .join("/")
    .slice(0, 200);
}

function deviceClass() {
  try {
    if (isTeslaBrowser()) return "car";
    if (isCoarsePointer()) return "touch";
  } catch (e) { /* ignore */ }
  return "desktop";
}

function observe(type, cb, opts) {
  try {
    const po = new PerformanceObserver((list) => {
      list.getEntries().forEach(cb);
    });
    po.observe({ type, buffered: true, ...(opts || {}) });
    return po;
  } catch (e) {
    return null;
  }
}

export function initVitals() {
  if (typeof window === "undefined" || window.__tm3VitalsInit) return;
  if (typeof PerformanceObserver === "undefined") return;
  window.__tm3VitalsInit = true;

  const v = { LCP: null, INP: null, CLS: 0, TTFB: null, FCP: null };
  let sawCls = false;

  observe("largest-contentful-paint", (e) => {
    v.LCP = e.startTime;
  });

  observe("paint", (e) => {
    if (e.name === "first-contentful-paint") v.FCP = e.startTime;
  });

  // CLS: total shift excluding those within 500ms of a user input. The full
  // spec windows this into sessions; the plain sum is close enough to tell a
  // stable layout from a jumping one, which is the question being asked.
  observe("layout-shift", (e) => {
    if (e.hadRecentInput) return;
    v.CLS += e.value;
    sawCls = true;
  });

  // INP approximated as the worst interaction latency seen. Good enough to
  // catch "tapping a tile in the car takes half a second".
  observe("event", (e) => {
    const d = e.duration;
    if (typeof d === "number" && (v.INP == null || d > v.INP)) v.INP = d;
  }, { durationThreshold: 40 });

  try {
    const nav = performance.getEntriesByType("navigation")[0];
    if (nav && typeof nav.responseStart === "number" && nav.responseStart > 0) {
      v.TTFB = nav.responseStart;
    }
  } catch (e) { /* ignore */ }

  let sent = false;
  function flush() {
    if (sent) return;
    sent = true;
    try {
      const vitals = [];
      const push = (metric, value) => {
        if (value == null) return;
        const val = metric === "CLS" ? Math.round(value * 1000) / 1000 : Math.round(value);
        vitals.push({ metric, value: val, rating: rate(metric, val) });
      };
      push("LCP", v.LCP);
      push("INP", v.INP);
      if (sawCls) push("CLS", v.CLS);
      push("TTFB", v.TTFB);
      push("FCP", v.FCP);
      if (!vitals.length) return;

      const conn = navigator.connection || {};
      const payload = {
        device: deviceClass(),
        path: routeOf(window.location.pathname),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        conn: String(conn.effectiveType || "").slice(0, 24),
        ua: String(navigator.userAgent || "").slice(0, 300),
        vitals,
      };
      const body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      } else {
        fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch (e) { /* telemetry must never break the page */ }
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
}

export default initVitals;
